import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './ProductPage.css';
import { CategoryMap } from '../../utils/categoryMap';

const apiURL = process.env.REACT_APP_API_URL;

const formatPriceForDisplay = (value) => {
  const numericPrice = Number(value);

  if (!Number.isFinite(numericPrice)) {
    return '0';
  }

  return numericPrice.toString().replace('.', ',');
};

const ProductPage = () => {
  const { productName } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(`${apiURL}/product/${encodeURIComponent(productName)}`);

        if (!response.ok) {
          const errorPayload = await response.json().catch(() => null);
          throw new Error(errorPayload?.message || 'No s’ha pogut carregar el producte');
        }

        const selectedProduct = await response.json();

        if (!selectedProduct) {
          throw new Error('Producte no trobat');
        }

        if (isMounted) {
          setProduct(selectedProduct);
          setSelectedImage(selectedProduct.image || '');
        }
      } catch (fetchError) {
        if (isMounted) {
          setError(fetchError.message || 'Ha ocorregut un error carregant el producte');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProduct();

    return () => {
      isMounted = false;
    };
  }, [productName]);

  const thumbnails = useMemo(() => {
    if (!Array.isArray(product?.images)) {
      return product?.image ? [product.image] : [];
    }

    const primary = product.image;
    const otherImages = product.images.filter((img) => img !== primary);
    const list = primary ? [primary, ...otherImages] : otherImages;
    if (product.video && !list.includes(product.video)) {
      list.push(product.video);
    }
    return list;
  }, [product]);

  if (loading) {
    return (
      <div className="product-page-loading">
        <p>Carregant producte...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-page-error">
        <p>{error}</p>
        <Link to="/" className="product-page-back-link">Tornar a la pàgina principal</Link>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const categoryKeys = Array.isArray(product.category) && product.category.length > 0
    ? product.category.filter(Boolean)
    : product.category
      ? [product.category].filter(Boolean)
      : [];
  const categoryLabels = CategoryMap.getDisplayNames(categoryKeys);
  const availabilityText = product.disponibility || 'No disponible';
  const currentImage = selectedImage || product.image;

  return (
    <div className="product-page">
      <div className="product-page-inner">
        <div className="product-page-header">
          <p className="product-page-breadcrumb">
            <Link to="/" className="product-page-breadcrumb-link">Home</Link>
            {categoryKeys.map((categoryKey, index) => (
              <span key={`${categoryKey}-${index}`}>
                <span> &gt; </span>
                <Link to={`/categories/${encodeURIComponent(categoryKey)}`} className="product-page-breadcrumb-link">
                  {CategoryMap.getLabel(categoryKey)}
                </Link>
              </span>
            ))}
            <span> &gt; </span>
            <span className="product-page-breadcrumb-current">{product.name}</span>
          </p>
        </div>

        <div className="product-page-grid">
          <div className="product-page-image-layout">
            {thumbnails.length > 1 && (
              <div className="product-page-thumbnails">
                {thumbnails.map((imageName, imageIndex) => {
                  const isActive = currentImage === imageName;

                  return (
                    <button
                      type="button"
                      key={`${imageName}-${imageIndex}`}
                      className={`product-page-thumbnail-button ${isActive ? 'product-page-thumbnail-button--active' : ''}`}
                      onClick={() => setSelectedImage(imageName)}
                      aria-label={`Mostra la imatge ${imageIndex + 1}`}
                    >
                      {product.video && imageName === product.video ? (
                        <video
                          src={`${imageName}`}
                          className="product-page-thumbnail"
                          muted
                          playsInline
                        />
                      ) : (
                        <img
                          src={`${imageName}`}
                          alt={`${product.name} - imatge ${imageIndex + 1}`}
                          className="product-page-thumbnail"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="product-page-image-column">
              {product.video && currentImage === product.video ? (
                <div className="product-page-video-wrapper">
                  <video
                    controls
                    className="product-page-video"
                    src={`${currentImage}`}
                  >
                    El teu navegador no suporta la reproducció de vídeo.
                  </video>
                </div>
              ) : (
                <img
                  src={`${currentImage}`}
                  alt={product.name}
                  className="product-page-main-image"
                />
              )}
            </div>
          </div>

          <div className="product-page-details">
            <p className="product-page-category">{categoryLabels.length > 0 ? categoryLabels.join(', ') : 'Sense categoria'}</p>
            <h1>{product.name}</h1>
            <p className="product-page-price">{formatPriceForDisplay(product.price)}€</p>
            <p className="product-page-description">{product.description || 'Sense descripció disponible.'}</p>

            <div className="product-page-meta">
              <p className={`product-page-stock ${product.inStock ? 'product-page-stock--in' : 'product-page-stock--out'}`}>
                {product.inStock ? (
                  <>
                    EN ESTOC
                    <span className="product-page-stock-icon" aria-hidden="true">✓</span>
                  </>
                ) : 'No disponible'}
              </p>
              {!product.inStock && (
                <p className="product-page-availability">
                  <strong>Disponibilitat:</strong> {availabilityText}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
